import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  RefreshCw, Users, MapPin, TrendingUp, TrendingDown, Megaphone,
  Search, X, ChevronDown, ChevronRight, AlertCircle, CheckCircle,
  Clock, BarChart3, Target, DollarSign, ExternalLink, Star, Trophy,
  UserPlus, AlertTriangle, Zap, Maximize2, Minimize2, Layers, Home,
} from "lucide-react";
import { toast } from "sonner";
import { MultiDogCard } from "@/components/campaigns/MultiDogCard";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Static segment config ────────────────────────────────────────────
const SEGMENTS = [
  { index: 0, label: "Never Visited", color: "#64748b", days: "Registered, 0 visits" },
  { index: 1, label: "Active",        color: "#22c55e", days: "Last 30 days" },
  { index: 2, label: "Cooling",       color: "#84cc16", days: "31–60 days ago" },
  { index: 3, label: "At-Risk",       color: "#eab308", days: "61–90 days ago" },
  { index: 4, label: "Lapsed",        color: "#f97316", days: "91–180 days ago" },
  { index: 5, label: "Dormant",       color: "#ef4444", days: "181–365 days ago" },
  { index: 6, label: "Churned",       color: "#9f1239", days: "365+ days ago" },
];

// ── Static campaign data ─────────────────────────────────────────────
const CAMPAIGNS = [
  {
    title: "Reactivation Email Blitz",
    channel: "Email",
    audience: "Lapsed customers (91–365 days)",
    cost: "$0",
    effort: "Low",
    impact: "High",
    color: "#ef4444",
    steps: [
      "Export lapsed segment from Gingr (last visit 91–365 days)",
      "3-email sequence: 'We miss [dog name]' → discount offer → last chance",
      "Subject line: '[Dog name] misses their friends at K9 Resorts!'",
      "Offer: 1 free daycare day with purchase of 5-day pack",
    ],
  },
  {
    title: "Never-Visited Conversion",
    channel: "Email + SMS",
    audience: "Registered but never booked",
    cost: "$0",
    effort: "Low",
    impact: "High",
    color: "#f97316",
    steps: [
      "These people signed up but never booked — intent exists",
      "Email: 'Your free meet & greet is waiting for [dog name]'",
      "SMS follow-up 3 days later with direct booking link",
      "Offer: first daycare day at 50% off",
    ],
  },
  {
    title: "Meta Radius Ads",
    channel: "Instagram + Facebook",
    audience: "Top feeder zip codes",
    cost: "$300–500/mo",
    effort: "Medium",
    impact: "High",
    color: "#8b5cf6",
    steps: [
      "Target top zip codes — highest customer concentration",
      "Creative: short Reels of dogs playing, testimonials",
      "Objective: Lead gen or traffic to booking page",
      "Budget: $10–15/day, run Tue–Thu",
    ],
  },
  {
    title: "Google Local Search Ads",
    channel: "Google (DIY)",
    audience: "'Dog daycare near me' searches",
    cost: "$800–1k/mo",
    effort: "Medium",
    impact: "High",
    color: "#22c55e",
    steps: [
      "Cancel agency — 100% of budget goes to actual ad spend",
      "Keywords: 'dog boarding [city]', 'dog daycare [city]'",
      "Enable Location extensions so address shows in ad",
      "Only a fraction of customers found you via search — huge upside",
    ],
  },
  {
    title: "Nextdoor Neighborhood Ads",
    channel: "Nextdoor",
    audience: "Top customer neighborhoods",
    cost: "$150–300/mo",
    effort: "Low",
    impact: "Medium",
    color: "#D4AF37",
    steps: [
      "Free Business Page post 2x/week in top neighborhoods",
      "Sponsor a post in top zip codes ($5–10/day)",
      "Post format: tip + K9 Resorts mention, not hard-sell",
      "Ask current customers in those zips to post a recommendation",
    ],
  },
  {
    title: "Referral Program",
    channel: "Email + In-Person",
    audience: "Active customers",
    cost: "~$35/referral",
    effort: "Low",
    impact: "Medium",
    color: "#38bdf8",
    steps: [
      "Existing customer referrals already work — formalize it",
      "Formalize: 'Give a friend a free day, get a free day yourself'",
      "Promote at check-in and via email to active segment",
      "Pure performance — only costs when it works",
    ],
  },
];

const ACTION_SEQUENCE = [
  {
    week: "Week 1–2",
    color: "#22c55e",
    title: "Zero-cost database activation",
    steps: [
      "Export lapsed (91–365d) list from Gingr",
      "Set up Mailchimp free tier or use existing email tool",
      "Send 3-email win-back to lapsed customers",
      "Send 'first visit' nudge to never-visited segment",
    ],
  },
  {
    week: "Week 3–4",
    color: "#D4AF37",
    title: "Cancel agency, take Google Ads in-house",
    steps: [
      "Give 30-day notice to marketing agency",
      "Create Google Ads account at ads.google.com",
      "Set up geo-targeted campaigns for top zip codes",
      "Redirect full budget to ad spend — no fees",
    ],
  },
  {
    week: "Month 2",
    color: "#60a5fa",
    title: "Launch Meta neighborhood campaign",
    steps: [
      "Set up Meta Business Manager",
      "Create 1 IG Reel: a day at K9 (30 sec)",
      "Boost to top zip codes, $10/day",
      "Launch Nextdoor Business Page + free posts",
    ],
  },
  {
    week: "Month 3",
    color: "#a78bfa",
    title: "Formalize referral program",
    steps: [
      "Email actives: 'Give a friend a free day, get a free day'",
      "Add referral CTA to check-in + follow-up emails",
      "Track which source drives new bookings monthly",
      "Shift budget toward what's working",
    ],
  },
];

// ── Seeded jitter for map dots (customers sharing a zip) ─────────────
function seededOffset(seed, spread = 0.022) {
  const r1 = Math.abs(Math.sin(seed * 1.7) * 10000) % 1;
  const r2 = Math.abs(Math.sin(seed * 3.1) * 10000) % 1;
  const angle = r1 * 2 * Math.PI;
  const radius = Math.sqrt(-2 * Math.log(Math.max(r2, 0.001))) * spread * 0.38;
  return [radius * Math.cos(angle), radius * Math.sin(angle)];
}

// ── Segment icon map (matches Leads Dashboard KPI icon style) ─────────
const SEGMENT_ICONS = [UserPlus, Star, TrendingDown, AlertCircle, Clock, Clock, AlertTriangle];

// ── Sub-components ────────────────────────────────────────────────────

// V3: lifecycle health = status ink on the number only (active ✓ green, cooling/at-risk amber, lapsed+ red).
const SEG_INK = { 1: "text-[#2F8F5F]", 2: "text-[#9A6B00]", 3: "text-[#9A6B00]", 4: "text-[#C2483A]", 5: "text-[#C2483A]", 6: "text-[#C2483A]" };

function SegmentKPI({ seg, total, onClick }) {
  const pct = total > 0 ? ((seg.count / total) * 100).toFixed(1) : "0.0";
  const Icon = SEGMENT_ICONS[seg.index] || Users;
  return (
    <button
      onClick={onClick}
      data-testid={`segment-pill-${seg.index}`}
      className="rounded-xl border border-[#1F8A7C]/25 bg-[#E6EFEF] transition-colors hover:border-[#1F8A7C]/50 p-4 text-left w-full"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{seg.label}</p>
          <p className={`v3-num text-[28px] font-bold mt-1 leading-none ${SEG_INK[seg.index] || "text-gray-900"}`}>{seg.count.toLocaleString()}</p>
        </div>
        <Icon className="h-5 w-5 text-gray-400 shrink-0 ml-2" />
      </div>
      <p className="text-gray-500 text-xs mt-3"><span className="v3-num">{pct}%</span> · {seg.days || SEGMENTS[seg.index]?.days}</p>
    </button>
  );
}

function CustomerDetailPanel({ customer, segments, gingrAppUrl, onClose }) {
  if (!customer) return null;
  const seg = segments[customer.segment] || segments[0];
  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const gingrUrl = gingrAppUrl && customer.gingr_owner_id
    ? `https://${gingrAppUrl}/owners/view/id/${customer.gingr_owner_id}`
    : null;

  return (
    <div
      data-testid="customer-detail-panel"
      className="border border-gray-200 rounded-2xl bg-white shadow-lg overflow-hidden flex flex-col"
      style={{ minHeight: 400 }}
    >
      {/* Header */}
      <div className="px-5 py-4 flex items-start justify-between" style={{ background: `${seg.color}18`, borderBottom: `3px solid ${seg.color}` }}>
        <div className="min-w-0 flex-1">
          <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ background: seg.color }}>
            {seg.label}
          </span>
          <h3 className="text-lg font-bold text-gray-900 mt-1">
            {customer.first_name} {customer.last_name}
          </h3>
          <p className="text-sm text-gray-500">{customer.city}{customer.zip ? `, ${customer.zip}` : ""}</p>
          {gingrUrl && (
            <a
              href={gingrUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-in-gingr-btn"
              className="inline-flex items-center gap-1.5 mt-2 text-xs font-bold px-3 py-1.5 rounded-lg text-white transition-all hover:opacity-90 hover:scale-105 shadow-sm"
              style={{ background: seg.color }}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View in Gingr
            </a>
          )}
          {!gingrUrl && customer.gingr_owner_id && (
            <p className="text-xs text-gray-400 mt-1.5">
              Gingr ID: {customer.gingr_owner_id}
            </p>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 ml-2 shrink-0">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Customer type strip: playgroup(s), service usage & distance */}
      <div className="px-5 pt-3 flex flex-wrap items-center gap-2" data-testid="detail-type-strip">
        {(customer.dog_groups || []).map((g) => {
          const GC = { large: "#059669", small: "#0284c7", pp: "#e11d48" };
          const GL = { large: "Large dog", small: "Small dog", pp: "Private Play" };
          return (
            <span key={g} className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ background: GC[g] || "#888" }}>
              {GL[g] || g}
            </span>
          );
        })}
        {customer.is_daycare && (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
            Daycare · {customer.daycare_visits}
          </span>
        )}
        {customer.is_boarding && (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
            Boarding · {customer.boarding_visits}
          </span>
        )}
        {customer.distance_mi != null && (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {customer.distance_mi} mi away
          </span>
        )}
      </div>

      {/* Fields */}
      <div className="px-5 py-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm overflow-y-auto flex-1">
        {[
          ["Email", customer.email || "—"],
          ["Phone", customer.phone || "—"],
          ["Address", customer.address ? `${customer.address}, ${customer.city}` : customer.city || "—"],
          ["Pets", customer.pets || "—"],
          ["Banned Animals", customer.banned_pets || "None"],
          ["Last Visit", fmt(customer.last_visit)],
          ["Next Booking", fmt(customer.next_booking)],
          ["Customer Since", fmt(customer.created_date)],
          ["Email Comms", customer.email_opted_in ? "Opted in" : "Opted out"],
          ["Marketing SMS", customer.marketing_sms ? "Yes" : "No"],
        ].map(([k, v]) => (
          <div key={k} className="col-span-2 sm:col-span-1">
            <div className="text-xs text-gray-400 font-medium">{k}</div>
            <div className="text-gray-800 font-medium truncate" title={v}>{v}</div>
          </div>
        ))}

        {/* Notes */}
        {customer.notes ? (
          <div className="col-span-2 mt-1">
            <div className="text-xs text-gray-400 font-medium mb-1">Staff Notes</div>
            <div
              className="text-sm text-gray-700 bg-yellow-50 border border-yellow-200 rounded-lg p-2.5 leading-relaxed"
            >
              {customer.notes}
            </div>
          </div>
        ) : customer.notes === "" ? null : null}
      </div>

      {/* Recommended action */}
      <div className="px-5 py-3 border-t border-gray-100" style={{ background: `${seg.color}08` }}>
        <div className="text-xs text-gray-400 font-medium mb-0.5">Recommended Action</div>
        <div className="text-sm font-semibold" style={{ color: seg.color }}>{seg.action}</div>
      </div>
    </div>
  );
}

// ── Map component ─────────────────────────────────────────────────────
function CustomerMap({ mapCustomers, unmappedCustomers = [], facility = null, segments, gingrAppUrl, onSelectCustomer, selectedCustomer,
  segFilter, setSegFilter, serviceFilter = [], setServiceFilter, groupFilter = [], setGroupFilter, toggleInArray }) {
  const mapRef = useRef(null);
  const instanceRef = useRef(null);
  const markersRef = useRef([]);
  const facilityMarkerRef = useRef(null);
  const baseLayersRef = useRef({ satellite: null, labels: null, street: null });
  const [ready, setReady] = useState(false);
  const [showUnmapped, setShowUnmapped] = useState(false);
  const [viewMode, setViewMode] = useState("satellite"); // "satellite" | "street"
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(link);
    }

    const init = () => {
      if (instanceRef.current || !mapRef.current || !window.L) return;
      const L = window.L;

      // Compute center from customer data
      const withCoords = mapCustomers.filter(c => c.lat && c.lng);
      let centerLat = 40.23, centerLng = -75.21, zoom = 11;
      if (withCoords.length > 0) {
        centerLat = withCoords.reduce((s, c) => s + c.lat, 0) / withCoords.length;
        centerLng = withCoords.reduce((s, c) => s + c.lng, 0) / withCoords.length;
      }
      // The facility is the anchor — if we know where the resort is, load the
      // map centered there so you immediately see how it's surrounded.
      const hasFacility = facility && facility.lat != null && facility.lng != null;
      if (hasFacility) {
        centerLat = facility.lat;
        centerLng = facility.lng;
        zoom = 12;
      }

      const map = L.map(mapRef.current, { zoomControl: true }).setView([centerLat, centerLng], zoom);

      // Base layers (created once, toggled by viewMode)
      const satellite = L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        { attribution: "Tiles © Esri", maxZoom: 19 }
      );
      const labels = L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
        { attribution: "Labels © Esri", maxZoom: 19, opacity: 0.85 }
      );
      const street = L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        { attribution: "© OpenStreetMap, © CARTO", maxZoom: 20 }
      );
      baseLayersRef.current = { satellite, labels, street };
      // Default = satellite
      satellite.addTo(map);
      labels.addTo(map);

      // Add customer dots. Gingr house-level coords ("address" precision) are
      // plotted exactly; only zip-centroid fallbacks get a small jitter so
      // same-zip customers don't fully overlap.
      withCoords.forEach((c, idx) => {
        const exact = c.geo_precision === "address";
        const [dlat, dlng] = exact ? [0, 0] : seededOffset((c.gingr_owner_id || idx) + idx);
        const seg = segments[c.segment] || segments[0];
        const marker = L.circleMarker([c.lat + dlat, c.lng + dlng], {
          radius: 6,
          fillColor: seg.color,
          color: "rgba(0,0,0,0.3)",
          weight: 1,
          fillOpacity: 0.85,
        }).addTo(map);
        marker.on("click", () => onSelectCustomer(c));
        markersRef.current.push({ marker, customer: c });
      });

      // Facility (resort) pin — distinct gold home marker, always on top, with a
      // popup showing the location name + address. Loads with the map.
      if (hasFacility) {
        const icon = L.divIcon({
          className: "facility-pin",
          html: `<div style="position:relative;transform:translate(-50%,-100%);">
            <div style="background:#c8971f;width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 8px rgba(0,0,0,0.4);border:2.5px solid #fff;display:flex;align-items:center;justify-content:center;">
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="transform:rotate(45deg)"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
            </div>
          </div>`,
          iconSize: [34, 34],
          iconAnchor: [0, 0],
        });
        const precisionNote = facility.geo_precision === "zip"
          ? '<div style="color:#b45309;font-size:11px;margin-top:3px">Approx. (zip-level) — set exact address in Location settings</div>'
          : "";
        const fmarker = L.marker([facility.lat, facility.lng], { icon, zIndexOffset: 1000 }).addTo(map);
        fmarker.bindPopup(
          `<div style="font-family:Arial,sans-serif;min-width:170px">
            <div style="font-weight:800;font-size:14px;color:#111">${(facility.name || "Facility").replace(/</g, "&lt;")}</div>
            <div style="font-size:12px;color:#555;margin-top:2px">${(facility.address || "").replace(/</g, "&lt;")}</div>
            <div style="font-size:11px;color:#c8971f;font-weight:700;margin-top:4px">★ Your resort</div>
            ${precisionNote}
          </div>`
        );
        facilityMarkerRef.current = fmarker;
      }

      instanceRef.current = map;
      setReady(true);
    };

    if (window.L) {
      init();
    } else {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
      script.onload = init;
      document.head.appendChild(script);
    }

    return () => {
      if (instanceRef.current) {
        instanceRef.current.remove();
        instanceRef.current = null;
        markersRef.current = [];
        facilityMarkerRef.current = null;
        baseLayersRef.current = { satellite: null, labels: null, street: null };
      }
    };
  }, [mapCustomers, facility]);

  // Switch base layer when viewMode changes
  useEffect(() => {
    if (!ready || !instanceRef.current) return;
    const map = instanceRef.current;
    const { satellite, labels, street } = baseLayersRef.current;
    if (!satellite) return;
    if (viewMode === "satellite") {
      if (street && map.hasLayer(street)) map.removeLayer(street);
      if (!map.hasLayer(satellite)) satellite.addTo(map);
      if (labels && !map.hasLayer(labels)) labels.addTo(map);
      if (labels) labels.bringToFront();
    } else {
      if (map.hasLayer(satellite)) map.removeLayer(satellite);
      if (labels && map.hasLayer(labels)) map.removeLayer(labels);
      if (!map.hasLayer(street)) street.addTo(map);
    }
    // keep dots above tiles
    markersRef.current.forEach(({ marker }) => marker.bringToFront && marker.bringToFront());
    if (facilityMarkerRef.current && facilityMarkerRef.current.setZIndexOffset) facilityMarkerRef.current.setZIndexOffset(1000);
  }, [viewMode, ready]);

  // Recompute map size when toggling fullscreen
  useEffect(() => {
    if (!ready || !instanceRef.current) return;
    const t = setTimeout(() => instanceRef.current && instanceRef.current.invalidateSize(), 250);
    return () => clearTimeout(t);
  }, [fullscreen, ready]);

  // Esc exits fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  // Filter visibility — mirror the Customers-tab filters (segment + service + group)
  useEffect(() => {
    if (!ready) return;
    markersRef.current.forEach(({ marker, customer }) => {
      const el = marker.getElement();
      if (!el) return;
      let show = true;
      if (segFilter !== null && segFilter !== undefined && customer.segment !== segFilter) show = false;
      if (show && serviceFilter.includes("daycare") && !customer.is_daycare) show = false;
      if (show && serviceFilter.includes("boarding") && !customer.is_boarding) show = false;
      if (show && groupFilter.length && !(customer.dog_groups || []).some((g) => groupFilter.includes(g))) show = false;
      el.style.display = show ? "" : "none";
    });
  }, [segFilter, serviceFilter, groupFilter, ready]);

  const withCoords = mapCustomers.filter(c => c.lat && c.lng);

  const visibleCount = withCoords.filter((c) => {
    if (segFilter !== null && segFilter !== undefined && c.segment !== segFilter) return false;
    if (serviceFilter.includes("daycare") && !c.is_daycare) return false;
    if (serviceFilter.includes("boarding") && !c.is_boarding) return false;
    if (groupFilter.length && !(c.dog_groups || []).some((g) => groupFilter.includes(g))) return false;
    return true;
  }).length;

  const filterPills = (
    <>
      <button
        onClick={() => { setSegFilter(null); setServiceFilter([]); setGroupFilter([]); }}
        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${segFilter === null && serviceFilter.length === 0 && groupFilter.length === 0 ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-600 border-gray-300 hover:border-gray-500"}`}
      >
        All ({withCoords.length})
      </button>
      {segments.map((seg) => (
        <button
          key={seg.index}
          onClick={() => setSegFilter(segFilter === seg.index ? null : seg.index)}
          className="px-3 py-1 rounded-full text-xs font-semibold border transition-all"
          style={{
            background: segFilter === seg.index ? seg.color : "white",
            color: segFilter === seg.index ? "white" : "#6b7280",
            borderColor: segFilter === seg.index ? seg.color : "#d1d5db",
          }}
        >
          {seg.label} ({seg.count || 0})
        </button>
      ))}
      <span className="w-px h-5 bg-gray-300 mx-1" />
      {[{ k: "daycare", label: "Daycare", c: "#2563eb" }, { k: "boarding", label: "Boarding", c: "#7c3aed" }].map((s) => (
        <button
          key={s.k}
          data-testid={`map-filter-service-${s.k}`}
          onClick={() => setServiceFilter(toggleInArray(serviceFilter, s.k))}
          className="px-3 py-1 rounded-full text-xs font-semibold border transition-all"
          style={{
            background: serviceFilter.includes(s.k) ? s.c : "white",
            color: serviceFilter.includes(s.k) ? "white" : "#6b7280",
            borderColor: serviceFilter.includes(s.k) ? s.c : "#d1d5db",
          }}
        >
          {s.label}
        </button>
      ))}
      {[{ k: "large", label: "Large", c: "#059669" }, { k: "small", label: "Small", c: "#0284c7" }, { k: "pp", label: "Private Play", c: "#e11d48" }].map((g) => (
        <button
          key={g.k}
          data-testid={`map-filter-group-${g.k}`}
          onClick={() => setGroupFilter(toggleInArray(groupFilter, g.k))}
          className="px-3 py-1 rounded-full text-xs font-semibold border transition-all"
          style={{
            background: groupFilter.includes(g.k) ? g.c : "white",
            color: groupFilter.includes(g.k) ? "white" : "#6b7280",
            borderColor: groupFilter.includes(g.k) ? g.c : "#d1d5db",
          }}
        >
          {g.label}
        </button>
      ))}
    </>
  );

  // Map overlay controls (view toggle + fullscreen)
  const hasFacilityPin = facility && facility.lat != null && facility.lng != null;
  const recenterOnResort = () => {
    if (instanceRef.current && hasFacilityPin) {
      instanceRef.current.setView([facility.lat, facility.lng], 13, { animate: true });
      if (facilityMarkerRef.current && facilityMarkerRef.current.openPopup) facilityMarkerRef.current.openPopup();
    }
  };
  const mapControls = (
    <div className="absolute top-3 right-3 z-[1000] flex items-center gap-2">
      {hasFacilityPin && (
        <button
          onClick={recenterOnResort}
          data-testid="map-recenter-resort"
          title={`Center on ${facility.name || "resort"}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg shadow-lg border border-amber-200 bg-white text-xs font-semibold text-amber-700 hover:bg-amber-50 transition-colors"
        >
          <Home className="h-3.5 w-3.5" /> Resort
        </button>
      )}
      <div className="flex rounded-lg overflow-hidden shadow-lg border border-gray-200 bg-white">
        <button
          onClick={() => setViewMode("satellite")}
          data-testid="map-view-satellite"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${viewMode === "satellite" ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}
        >
          <Layers className="h-3.5 w-3.5" /> Satellite
        </button>
        <button
          onClick={() => setViewMode("street")}
          data-testid="map-view-street"
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${viewMode === "street" ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}
        >
          <MapPin className="h-3.5 w-3.5" /> Map
        </button>
      </div>
      <button
        onClick={() => setFullscreen((v) => !v)}
        data-testid="map-fullscreen-toggle"
        title={fullscreen ? "Exit full screen (Esc)" : "Full screen"}
        className="flex items-center justify-center h-8 w-8 rounded-lg shadow-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 transition-colors"
      >
        {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </button>
    </div>
  );

  const mapHeight = fullscreen ? "100%" : 620;

  return (
    <div className="space-y-3">
      {/* Filter bar (normal mode) */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm font-medium text-gray-500">Filter:</span>
        {filterPills}
      </div>

      <div
        className={fullscreen
          ? "fixed inset-0 z-[999] bg-white flex gap-3 p-3"
          : "flex gap-4"}
        style={fullscreen ? {} : { minHeight: 620 }}
      >
        {/* Map */}
        <div
          className={`relative flex-1 rounded-2xl overflow-hidden border border-gray-200 shadow-md ${fullscreen ? "h-full" : ""}`}
          style={fullscreen ? {} : { minHeight: 620 }}
        >
          <div ref={mapRef} style={{ height: mapHeight, width: "100%" }} data-testid="customer-map" />
          {mapControls}

          {/* Floating filter bar (fullscreen only) */}
          {fullscreen && (
            <div className="absolute top-3 left-3 z-[1000] max-w-[70%] flex flex-wrap gap-1.5 p-2 rounded-xl bg-white/90 backdrop-blur shadow-lg border border-gray-200">
              {filterPills}
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div style={{ width: 280, flexShrink: 0 }} className={fullscreen ? "h-full overflow-y-auto" : ""}>
          {selectedCustomer ? (
            <CustomerDetailPanel
              customer={selectedCustomer}
              segments={segments}
              gingrAppUrl={gingrAppUrl}
              onClose={() => onSelectCustomer(null)}
            />
          ) : (
            <div className="h-full border border-gray-200 rounded-2xl bg-gray-50 flex flex-col items-center justify-center text-center p-6">
              <MapPin className="h-10 w-10 text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-400">Click any dot to view customer profile</p>
            </div>
          )}
        </div>
      </div>

      {/* Precision summary + unmapped disclosure */}
      <div className="space-y-2" data-testid="map-coverage-summary">
        <p className="text-xs text-gray-500 text-center">
          {visibleCount.toLocaleString()} of {(withCoords.length + unmappedCustomers.length).toLocaleString()} customers shown on map
          {unmappedCustomers.length > 0 && (
            <> · {unmappedCustomers.length} couldn't be placed</>
          )}
        </p>

        {unmappedCustomers.length > 0 && (
          <div className="border border-amber-200 bg-amber-50 rounded-xl overflow-hidden">
            <button
              onClick={() => setShowUnmapped((v) => !v)}
              data-testid="toggle-unmapped-customers"
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
            >
              <span className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {unmappedCustomers.length} customer{unmappedCustomers.length === 1 ? "" : "s"} not on the map (bad/missing address in Gingr)
              </span>
              {showUnmapped ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {showUnmapped && (
              <div className="max-h-64 overflow-y-auto border-t border-amber-200 bg-white">
                {unmappedCustomers.map((u, i) => (
                  <div
                    key={u.gingr_owner_id || i}
                    data-testid="unmapped-customer-row"
                    className="flex items-center justify-between px-4 py-2 text-sm border-b border-gray-100 last:border-0"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 truncate">
                        {u.first_name} {u.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{u.reason}</p>
                    </div>
                    {gingrAppUrl && u.gingr_owner_id && (
                      <a
                        href={`https://${gingrAppUrl}/owners/view/id/${u.gingr_owner_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-3 shrink-0 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Fix in Gingr →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Cohort Card component ─────────────────────────────────────────────
function CohortCard({ cohort, segments, gingrAppUrl, onSelectCustomer }) {
  const [expanded, setExpanded] = useState(false);
  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "—";

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
      style={{ borderTop: "1px solid rgba(31,138,124,0.14)" }}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span
                className="v3-num text-xl font-bold text-gray-900"
              >
                {cohort.count.toLocaleString()}
              </span>
              <h3 className="font-bold text-gray-800 text-sm">{cohort.label}</h3>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{cohort.description}</p>
          </div>
        </div>

        {/* Action */}
        <div
          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg mb-3 inline-block"
          style={{ background: "#E6F6F3", color: "#1F8A7C" }}
        >
          Action: {cohort.action}
        </div>
        {!/never|currently booked/i.test(cohort.label) && (
        <Link
          to={`/campaigns?template=${/daycare/i.test(cohort.label) ? "daycare_first_boarding" : /win|lapsed|dormant|risk|cool|churn/i.test(cohort.label) ? "lapsed_boarders" : "multi_dog_holiday"}`}
          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg mb-3 ml-2 inline-block border"
          style={{ borderColor: "#1F8A7C", color: "#1F8A7C" }}
          data-testid={`cohort-build-campaign-${cohort.label.toLowerCase().replace(/\W+/g, "-")}`}
        >
          Build campaign
        </Link>
        )}

        {/* Customer list preview */}
        {cohort.customers.length > 0 && (
          <>
            <div className="space-y-1">
              {(expanded ? cohort.customers : cohort.customers.slice(0, 5)).map((c, i) => {
                const seg = segments[c.segment] || segments[0];
                return (
                  <div
                    key={c.gingr_owner_id || i}
                    className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-gray-50 cursor-pointer group"
                    onClick={() => onSelectCustomer(c)}
                    data-testid={`cohort-row-${cohort.label}-${i}`}
                  >
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-full font-bold text-white shrink-0"
                      style={{ background: seg.color, fontSize: 10 }}
                    >
                      {seg.label[0]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-800 group-hover:text-blue-600 truncate block">
                        {c.first_name} {c.last_name}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 shrink-0 flex items-center gap-2">
                      {c.pet_count > 0 && <span>{c.pet_count} dog{c.pet_count !== 1 ? "s" : ""}</span>}
                      {c.last_visit && <span>{fmt(c.last_visit)}</span>}
                    </div>
                    {gingrAppUrl && c.gingr_owner_id && (
                      <a
                        href={`https://${gingrAppUrl}/owners/view/id/${c.gingr_owner_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="opacity-0 group-hover:opacity-100 text-xs font-semibold px-2 py-0.5 rounded-md border transition-all shrink-0"
                        style={{ color: "#1F8A7C", borderColor: "rgba(31,138,124,0.3)", background: "#F5F9F9" }}
                        title="View in Gingr"
                      >
                        <ExternalLink className="h-3 w-3 inline-block" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
            {cohort.customers.length > 5 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mt-2 text-xs font-semibold flex items-center gap-1 transition-colors"
                style={{ color: "#1F8A7C" }}
                data-testid={`cohort-expand-${cohort.label}`}
              >
                {expanded
                  ? <><ChevronDown className="h-3.5 w-3.5" />Show less</>
                  : <><ChevronRight className="h-3.5 w-3.5" />See all {cohort.count.toLocaleString()} ({cohort.customers.length - 5} more shown)</>
                }
              </button>
            )}
          </>
        )}
        {cohort.count === 0 && (
          <p className="text-sm text-gray-400 italic">No customers in this cohort yet.</p>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────
export default function CustomerIntelligence() {
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [mapCustomers, setMapCustomers] = useState([]);
  const [unmappedCustomers, setUnmappedCustomers] = useState([]);
  const [facility, setFacility] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [gingrAppUrl, setGingrAppUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [segFilter, setSegFilter] = useState(null);
  const [serviceFilter, setServiceFilter] = useState([]); // subset of ['daycare','boarding'] — ALL selected must be used
  const [groupFilter, setGroupFilter] = useState([]);     // subset of ['large','small','pp'] — ANY match
  const [distanceSort, setDistanceSort] = useState(null); // null | 'asc' | 'desc'
  const [customersLoading, setCustomersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCampaign, setActiveCampaign] = useState(null);
  const pollRef = useRef(null);

  // Load stats + sync status on mount
  useEffect(() => {
    loadOverview();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Load customers when tab becomes active
  useEffect(() => {
    if (activeTab === "customers" && customers.length === 0 && !loading) loadCustomers();
    if (activeTab === "map" && mapCustomers.length === 0 && !loading) loadMapData();
    if (activeTab === "cohorts" && !cohorts && !loading) loadCohorts();
  }, [activeTab, loading]);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const [statsRes, syncRes] = await Promise.all([
        axios.get(`${API}/api/customer-intelligence/stats`),
        axios.get(`${API}/api/customer-intelligence/sync-status`),
      ]);
      setStats(statsRes.data);
      const s = syncRes.data;
      setSyncStatus(s);
      if (s.gingr_app_url) setGingrAppUrl(s.gingr_app_url);
      if (s.status === "syncing") {
        setSyncing(true);
        startPolling();
      } else {
        const staleMs = 6 * 60 * 60 * 1000;
        const isStale = !s.last_sync || (Date.now() - new Date(s.last_sync).getTime()) > staleMs;
        const needsSync = s.status === "never_synced" || (isStale && s.status !== "error");
        if (needsSync) triggerSync(false);
      }
    } catch (err) {
      console.error("CI load error:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    setCustomersLoading(true);
    try {
      // Load the full list once; all filtering (segment/service/group/search/
      // distance) happens client-side so multiple dimensions can be combined.
      const res = await axios.get(`${API}/api/customer-intelligence/customers`, { params: { limit: 5000 } });
      setCustomers(res.data.customers || []);
    } catch (err) {
      console.error("CI customers error:", err);
    } finally {
      setCustomersLoading(false);
    }
  };

  const loadMapData = async () => {
    try {
      const res = await axios.get(`${API}/api/customer-intelligence/map`);
      setMapCustomers(res.data.customers || []);
      setUnmappedCustomers(res.data.unmapped || []);
      setFacility(res.data.facility || null);
    } catch (err) {
      console.error("CI map error:", err);
    }
  };

  const loadCohorts = async () => {
    try {
      const res = await axios.get(`${API}/api/customer-intelligence/cohorts`);
      setCohorts(res.data);
    } catch (err) {
      console.error("CI cohorts error:", err);
    }
  };

  const triggerSync = async (manual = true) => {
    try {
      await axios.post(`${API}/api/customer-intelligence/sync`);
      setSyncing(true);
      if (manual) toast.info("Syncing customers from Gingr…");
      startPolling();
    } catch (err) {
      if (manual) toast.error(err.response?.data?.detail || "Sync failed");
    }
  };

  const resetSync = async () => {
    try {
      await axios.post(`${API}/api/customer-intelligence/sync/reset`);
      if (pollRef.current) clearInterval(pollRef.current);
      setSyncing(false);
      toast.success("Stuck sync cleared. Click Sync from Gingr to retry.");
      // refresh status so the UI leaves the syncing spinner
      const res = await axios.get(`${API}/api/customer-intelligence/sync-status`);
      setSyncStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/customer-intelligence/sync-status`);
        const s = res.data;
        setSyncStatus(s);
        if (s.status !== "syncing") {
          clearInterval(pollRef.current);
          setSyncing(false);
          if (s.status === "success") {
            toast.success(`Synced ${s.customer_count?.toLocaleString()} customers`);
            if (s.gingr_app_url) setGingrAppUrl(s.gingr_app_url);
            const statsRes = await axios.get(`${API}/api/customer-intelligence/stats`);
            setStats(statsRes.data);
            setCustomers([]);
            setMapCustomers([]);
            setCohorts(null);
          } else if (s.status === "error") {
            toast.error(`Sync error: ${s.error}`);
          }
        }
      } catch { /* silent */ }
    }, 4000);
  };

  const handleSegFilterChange = (idx) => {
    setSegFilter((prev) => (prev === idx ? null : idx));
    setSelectedCustomer(null);
  };

  const handleSearch = useCallback((q) => {
    setSearchTerm(q);
    setSelectedCustomer(null);
  }, []);

  const toggleInArray = (arr, val) => (arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  // Client-side multi-filter: segment (single) AND service (all selected used)
  // AND group (any dog matches) AND search, with optional distance sort.
  const filteredCustomers = (() => {
    const q = (searchTerm || "").trim().toLowerCase();
    let list = customers.filter((c) => {
      if (segFilter !== null && c.segment !== segFilter) return false;
      if (serviceFilter.includes("daycare") && !c.is_daycare) return false;
      if (serviceFilter.includes("boarding") && !c.is_boarding) return false;
      if (groupFilter.length && !(c.dog_groups || []).some((g) => groupFilter.includes(g))) return false;
      if (q) {
        const hay = `${c.first_name} ${c.last_name} ${c.email} ${c.pets} ${c.city}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    if (distanceSort) {
      list = [...list].sort((a, b) => {
        const da = a.distance_mi == null ? Infinity : a.distance_mi;
        const db = b.distance_mi == null ? Infinity : b.distance_mi;
        return distanceSort === "asc" ? da - db : db - da;
      });
    }
    return list;
  })();

  const exportCustomersCSV = () => {
    const cols = ["first_name", "last_name", "email", "phone", "pets", "city", "zip",
      "segment", "daycare_visits", "boarding_visits", "dog_groups", "distance_mi", "last_visit", "next_booking"];
    const segName = (i) => (segments[i] ? segments[i].label : i);
    const esc = (v) => {
      const s = Array.isArray(v) ? v.join("|") : (v == null ? "" : String(v));
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["First", "Last", "Email", "Phone", "Pets", "City", "Zip",
      "Segment", "Daycare Visits", "Boarding Visits", "Dog Groups", "Miles Away", "Last Visit", "Next Booking"];
    const rows = filteredCustomers.map((c) =>
      cols.map((k) => esc(k === "segment" ? segName(c[k]) : c[k])).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ci-customers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Format last sync time
  const lastSyncLabel = () => {
    if (!syncStatus?.last_sync) return null;
    const diff = Math.round((Date.now() - new Date(syncStatus.last_sync).getTime()) / 60000);
    if (diff < 1) return "just now";
    if (diff < 60) return `${diff}m ago`;
    const h = Math.round(diff / 60);
    return `${h}h ago`;
  };

  const segments = stats?.segments || SEGMENTS.map((s) => ({ ...s, count: 0 }));
  const total = stats?.total || 0;

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "customers", label: "Customers", icon: Users },
    { id: "map", label: "Map", icon: MapPin },
    { id: "cohorts", label: "Cohorts", icon: Trophy },
    { id: "campaigns", label: "Campaigns", icon: Megaphone },
  ];

  return (
    <div className="ops-v3 space-y-6" data-testid="customer-intelligence-page">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-[32px] font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Customer Intelligence
          </h1>
          <p className="text-sm sm:text-base text-gray-500 mt-0.5">
            Lifecycle segments, geographic insights &amp; campaign playbooks
          </p>
        </div>

        {/* Sync control */}
        <div className="flex items-center gap-3">
          {gingrAppUrl && (
            <a
              href={`https://${gingrAppUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gingr-app-link"
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
            >
              <ExternalLink className="h-4 w-4 text-gray-500" />
              Open Gingr
            </a>
          )}
          {syncStatus && (
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              {syncing ? (
                <><RefreshCw className="h-3.5 w-3.5 animate-spin text-blue-500" /><span className="text-blue-600 font-medium">Syncing…{syncStatus.stalled_seconds > 900 ? ` (stalled ${Math.floor(syncStatus.stalled_seconds / 60)}m)` : ""}</span></>
              ) : syncStatus.status === "success" ? (
                <><CheckCircle className="h-3.5 w-3.5 text-green-500" /><span>Last synced {lastSyncLabel()}</span></>
              ) : syncStatus.status === "error" ? (
                <><AlertCircle className="h-3.5 w-3.5 text-red-500" /><span className="text-red-500">Sync error</span></>
              ) : (
                <><Clock className="h-3.5 w-3.5" /><span>Never synced</span></>
              )}
            </div>
          )}
          {syncing && syncStatus?.stalled_seconds > 900 && (
            <button
              onClick={resetSync}
              data-testid="ci-reset-sync-button"
              title="Force-clear a stalled sync so you can retry"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 transition-all"
            >
              <AlertCircle className="h-4 w-4" />
              Reset stuck sync
            </button>
          )}
          <button
            onClick={() => triggerSync(true)}
            disabled={syncing}
            data-testid="sync-button"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
            style={{ background: "#1F8A7C" }}
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            Sync from Gingr
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`ci-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                active ? "bg-[#1F8A7C] text-white" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 animate-pulse">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="h-28 bg-gray-200 rounded-xl" />
          ))}
        </div>
      )}

      {/* Syncing notice (no data yet) */}
      {!loading && syncing && total === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
          <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-3" />
          <p className="font-semibold text-blue-700">Syncing customer data from Gingr…</p>
          <p className="text-sm text-blue-500 mt-1">This may take 1–2 minutes on first run. Data will appear automatically.</p>
        </div>
      )}

      {/* ── OVERVIEW TAB ── */}
      {!loading && activeTab === "overview" && (
        <div className="space-y-6">
          {/* Segment KPI tiles — Leads-Dashboard style gradient cards */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Customer Lifecycle Segments — {total.toLocaleString()} total
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
              {segments.map((seg) => (
                <SegmentKPI key={seg.index} seg={seg} total={total} onClick={() => { setActiveTab("customers"); handleSegFilterChange(seg.index); }} />
              ))}
            </div>
          </div>

          {/* Occupancy gap */}
          {total > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
                <Target className="h-5 w-5 text-orange-500" />
                Occupancy Gap to 70%
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Convert just{" "}
                <strong className="text-orange-600">
                  {((24 / Math.max(total, 1)) * 100).toFixed(1)}% of your database
                </strong>{" "}
                back to active customers before spending on new acquisition.
              </p>
              <div className="space-y-3">
                {[
                  { label: "Current (~40%)", value: 40, color: "#ef4444" },
                  { label: "Target (70%)", value: 70, color: "#D4AF37" },
                  { label: "Capacity (100%)", value: 100, color: "#d1d5db" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-32 shrink-0">{label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${value}%`, background: color }}
                      />
                    </div>
                    <span className="text-xs font-bold text-gray-700 w-8">{value}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Two-column charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Comm Preferences / Campaign Reach */}
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-1">Campaign Reach Analysis</h3>
              <p className="text-xs text-gray-400 mb-4">
                How many of your {total.toLocaleString()} customers you can reach via each channel.{" "}
                <span className="text-gray-300">Opt-in data reflects what's stored in Gingr per customer.</span>
              </p>
              <div className="space-y-3">
                {[
                  { label: "Email Opt-In", key: "email_reach", color: "#0ea5e9", note: "Can receive emails" },
                  { label: "Marketing Email", key: "marketing_email", color: "#6366f1", note: "Active marketing list" },
                  { label: "SMS / Text", key: "sms_reach", color: "#22c55e", note: "Can receive texts" },
                  { label: "Currently Booked", key: "has_next_booking", color: "#D4AF37", note: "Upcoming reservation" },
                  { label: "Multi-Dog Household", key: "multi_dog", color: "#8b5cf6", note: "2+ pets registered" },
                ].map(({ label, key, color, note }) => {
                  const count = stats?.comm_preferences?.[key] || 0;
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div className="w-36 shrink-0">
                        <div className="text-xs font-semibold text-gray-700">{label}</div>
                        <div className="text-xs text-gray-400">{note}</div>
                      </div>
                      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, background: color }}
                        />
                      </div>
                      <span className="text-sm font-bold text-gray-700 w-12 text-right">
                        {count.toLocaleString()}
                      </span>
                      <span className="text-xs text-gray-400 w-8">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Zip Codes */}
            {stats?.top_zips?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                <h3 className="font-bold text-gray-800 mb-4">Top Zip Codes</h3>
                <div className="space-y-2">
                  {stats.top_zips.map((z, i) => (
                    <div key={z.zip} className="flex items-center gap-3">
                      <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                      <div className="flex-1">
                        <div className="flex justify-between mb-0.5">
                          <span className="text-sm font-semibold text-gray-700">
                            {z.city || z.zip}
                          </span>
                          <span className="text-sm font-bold text-gray-900">{z.count}</span>
                        </div>
                        <div className="bg-gray-100 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600"
                            style={{ width: `${(z.count / (stats.top_zips[0]?.count || 1)) * 100}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{z.zip}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Key Insights */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
            <h3 className="font-bold text-gray-800 mb-4">Key Insights</h3>
            <div className="space-y-4">
              {[
                {
                  n: "01",
                  color: "#0ea5e9",
                  title: `${((segments[5]?.count || 0) + (segments[4]?.count || 0)).toLocaleString()} warm leads already in your database`,
                  body: `${segments[4]?.count || 0} lapsed + ${segments[5]?.count || 0} dormant customers. An email/SMS win-back costs $0 and could close your entire occupancy gap before spending a dollar on new acquisition.`,
                },
                {
                  n: "02",
                  color: "#D4AF37",
                  title: `${segments[0]?.count || 0} registered but never booked`,
                  body: "These people showed intent by signing up. A single 'first visit' nudge email with a 50% off offer often converts 5–10% immediately.",
                },
                {
                  n: "03",
                  color: "#22c55e",
                  title: "Driving by is still your #1 acquisition channel",
                  body: "Meta radius ads and Google Local ARE the digital version of 'drove by' — your ad budget needs to be focused on the zip codes already producing the most customers.",
                },
              ].map(({ n, color, title, body }) => (
                <div key={n} className="flex gap-4">
                  <span
                    className="text-lg font-extrabold shrink-0"
                    style={{ color, minWidth: 28 }}
                  >
                    {n}
                  </span>
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">{title}</div>
                    <div className="text-sm text-gray-500 mt-0.5">{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── CUSTOMERS TAB ── */}
      {!loading && activeTab === "customers" && (
        <div className="flex gap-5" style={{ minHeight: 600 }}>
          {/* List panel */}
          <div className="flex-1 flex flex-col gap-3 min-w-0">
            {/* Segment filter pills */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleSegFilterChange(null)}
                data-testid="segment-filter-all"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${segFilter === null ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-600 border-gray-300 hover:border-gray-600"}`}
              >
                All ({total.toLocaleString()})
              </button>
              {segments.map((seg) => (
                <button
                  key={seg.index}
                  onClick={() => handleSegFilterChange(seg.index)}
                  data-testid={`seg-filter-${seg.index}`}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
                  style={{
                    background: segFilter === seg.index ? seg.color : "white",
                    color: segFilter === seg.index ? "white" : "#6b7280",
                    borderColor: segFilter === seg.index ? seg.color : "#d1d5db",
                  }}
                >
                  {seg.label} ({seg.count || 0})
                </button>
              ))}
            </div>

            {/* Service usage + Dog group + Distance filters (combine with segment) */}
            <div className="flex flex-wrap items-center gap-2" data-testid="ci-type-filters">
              <span className="text-xs font-semibold text-gray-400 uppercase mr-1">Uses:</span>
              {[{ k: "daycare", label: "Daycare" }, { k: "boarding", label: "Boarding" }].map((s) => (
                <button
                  key={s.k}
                  onClick={() => setServiceFilter((p) => toggleInArray(p, s.k))}
                  data-testid={`filter-service-${s.k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${serviceFilter.includes(s.k) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"}`}
                >
                  {s.label}
                </button>
              ))}
              <span className="text-xs font-semibold text-gray-400 uppercase mx-1">Group:</span>
              {[{ k: "large", label: "Large", c: "#059669" }, { k: "small", label: "Small", c: "#0284c7" }, { k: "pp", label: "Private Play", c: "#e11d48" }].map((g) => (
                <button
                  key={g.k}
                  onClick={() => setGroupFilter((p) => toggleInArray(p, g.k))}
                  data-testid={`filter-group-${g.k}`}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
                  style={{
                    background: groupFilter.includes(g.k) ? g.c : "white",
                    color: groupFilter.includes(g.k) ? "white" : "#6b7280",
                    borderColor: groupFilter.includes(g.k) ? g.c : "#d1d5db",
                  }}
                >
                  {g.label}
                </button>
              ))}
              <button
                onClick={() => setDistanceSort((p) => (p === "desc" ? "asc" : p === "asc" ? null : "desc"))}
                data-testid="filter-distance-sort"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1 ${distanceSort ? "bg-amber-500 text-white border-amber-500" : "bg-white text-gray-600 border-gray-300 hover:border-amber-400"}`}
              >
                <MapPin className="h-3 w-3" /> Distance {distanceSort === "desc" ? "▼ far" : distanceSort === "asc" ? "▲ near" : ""}
              </button>
              {(serviceFilter.length > 0 || groupFilter.length > 0 || distanceSort || segFilter !== null || searchTerm) && (
                <button
                  onClick={() => { setServiceFilter([]); setGroupFilter([]); setDistanceSort(null); setSegFilter(null); setSearchTerm(""); }}
                  data-testid="filter-clear"
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-300 bg-white text-gray-500 hover:text-gray-800"
                >
                  Clear
                </button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-gray-500" data-testid="ci-filtered-count">{filteredCustomers.length.toLocaleString()} match</span>
                <button
                  onClick={exportCustomersCSV}
                  data-testid="export-customers-csv"
                  disabled={filteredCustomers.length === 0}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  Export CSV
                </button>
              </div>
            </div>


            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, pet, city…"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                data-testid="customer-search"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm && (
                <button onClick={() => handleSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex-1">
              <div className="grid text-xs font-semibold text-gray-400 uppercase px-4 py-2 border-b border-gray-100 bg-gray-50"
                style={{ gridTemplateColumns: "1fr 96px 92px 84px 70px 74px" }}>
                <span>Customer</span>
                <span>Group</span>
                <span>Uses</span>
                <span>Last Visit</span>
                <span>Miles</span>
                <span>Segment</span>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: 480 }}>
                {customersLoading ? (
                  <div className="p-8 text-center text-gray-400">
                    <RefreshCw className="h-5 w-5 animate-spin inline-block mr-2" />Loading customers…
                  </div>
                ) : filteredCustomers.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    {syncing ? "Syncing customer data…" : "No customers match your filters."}
                  </div>
                ) : (
                  filteredCustomers.slice(0, 300).map((c, i) => {
                    const seg = segments[c.segment] || segments[0];
                    const GC = { large: "#059669", small: "#0284c7", pp: "#e11d48" };
                    const GL = { large: "L", small: "S", pp: "PP" };
                    return (
                      <div
                        key={c.gingr_owner_id || i}
                        onClick={() => setSelectedCustomer(selectedCustomer?.gingr_owner_id === c.gingr_owner_id ? null : c)}
                        data-testid={`customer-row-${i}`}
                        className="grid cursor-pointer border-b border-gray-50 hover:bg-blue-50 transition-colors py-2.5 px-4"
                        style={{
                          gridTemplateColumns: "1fr 96px 92px 84px 70px 74px",
                          background: selectedCustomer?.gingr_owner_id === c.gingr_owner_id ? "#eff6ff" : undefined,
                        }}
                      >
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-800 text-sm truncate">
                            {c.first_name} {c.last_name}
                          </div>
                          <div className="text-xs text-gray-400 truncate">{c.pets || c.email}</div>
                        </div>
                        <div className="self-center flex flex-wrap gap-1">
                          {(c.dog_groups || []).length ? (c.dog_groups || []).map((g) => (
                            <span key={g} className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white" style={{ background: GC[g] || "#888" }} title={g}>{GL[g] || g}</span>
                          )) : <span className="text-xs text-gray-300">—</span>}
                        </div>
                        <div className="self-center text-xs text-gray-600">
                          {c.is_daycare && <span className="text-blue-700 font-semibold">DC {c.daycare_visits}</span>}
                          {c.is_daycare && c.is_boarding && <span className="text-gray-300"> · </span>}
                          {c.is_boarding && <span className="text-purple-700 font-semibold">BD {c.boarding_visits}</span>}
                          {!c.is_daycare && !c.is_boarding && <span className="text-gray-300">—</span>}
                        </div>
                        <div className="text-xs text-gray-600 self-center">
                          {c.last_visit ? new Date(c.last_visit).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "Never"}
                        </div>
                        <div className="text-xs text-gray-600 self-center">
                          {c.distance_mi != null ? `${c.distance_mi} mi` : "—"}
                        </div>
                        <div className="self-center">
                          <span
                            className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                            style={{ background: seg.color }}
                          >
                            {seg.label.split(" ")[0]}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
                {filteredCustomers.length > 300 && (
                  <div className="p-3 text-center text-xs text-gray-400">
                    Showing 300 of {filteredCustomers.length} — refine your filters to narrow results
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Detail panel */}
          <div style={{ width: 300, flexShrink: 0 }}>
            {selectedCustomer ? (
              <CustomerDetailPanel
                customer={selectedCustomer}
                segments={segments}
                gingrAppUrl={gingrAppUrl}
                onClose={() => setSelectedCustomer(null)}
              />
            ) : (
              <div className="h-full border border-gray-200 rounded-2xl bg-gray-50 flex flex-col items-center justify-center text-center p-6 min-h-64">
                <Users className="h-10 w-10 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-400">Click any row to see the full customer profile</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MAP TAB ── */}
      {!loading && activeTab === "map" && (
        <div>
          {mapCustomers.length === 0 && !syncing ? (
            <div className="text-center py-16 text-gray-400">
              <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No geocoded customers yet.</p>
              <p className="text-sm mt-1">Sync from Gingr first to populate the map.</p>
            </div>
          ) : (
            <CustomerMap
              mapCustomers={mapCustomers}
              unmappedCustomers={unmappedCustomers}
              facility={facility}
              segments={segments}
              gingrAppUrl={gingrAppUrl}
              onSelectCustomer={setSelectedCustomer}
              selectedCustomer={selectedCustomer}
              segFilter={segFilter}
              setSegFilter={setSegFilter}
              serviceFilter={serviceFilter}
              setServiceFilter={setServiceFilter}
              groupFilter={groupFilter}
              setGroupFilter={setGroupFilter}
              toggleInArray={toggleInArray}
            />
          )}
        </div>
      )}

      {/* ── COHORTS TAB ── */}
      {!loading && activeTab === "cohorts" && (
        <div className="space-y-5">
          {!cohorts ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
              <RefreshCw className="h-5 w-5 animate-spin" />
              <span>Loading cohort analysis…</span>
            </div>
          ) : (
            <>
              <MultiDogCard />
              <p className="text-sm text-gray-500">
                Automatically grouped from your {total.toLocaleString()} customers — click any row to see the full profile.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {Object.values(cohorts).map((cohort) => (
                  <CohortCard
                    key={cohort.label}
                    cohort={cohort}
                    segments={segments}
                    gingrAppUrl={gingrAppUrl}
                    onSelectCustomer={(c) => { setSelectedCustomer(c); setActiveTab("customers"); }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── CAMPAIGNS TAB ── */}
      {!loading && activeTab === "campaigns" && (
        <div className="space-y-6">
          {/* Agency vs DIY comparison */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
              <div className="text-xs font-semibold text-red-400 uppercase mb-1">Current Agency Model</div>
              <div className="text-3xl font-extrabold text-red-600">$4,000<span className="text-base font-medium">/mo</span></div>
              <div className="text-sm text-red-500 mt-1">$3k agency + $1k Google (agency-managed)</div>
            </div>
            <div className="flex flex-col items-center justify-center gap-1">
              <div className="text-2xl font-black text-gray-400">→</div>
              <div className="text-center">
                <div className="text-xl font-extrabold text-green-600">$2,750 saved</div>
                <div className="text-xs text-gray-400">per month</div>
                <div className="text-xs font-bold text-green-700 mt-0.5">$33k/year</div>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
              <div className="text-xs font-semibold text-green-600 uppercase mb-1">DIY Diversified Model</div>
              <div className="text-3xl font-extrabold text-green-700">$1,250<span className="text-base font-medium">/mo</span></div>
              <div className="text-sm text-green-600 mt-1">$1k Google (direct) + $150 Meta + $100 Nextdoor</div>
              <div className="text-xs text-green-500 mt-1">+ $0 email campaigns from your existing database</div>
            </div>
          </div>

          {/* Campaign cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CAMPAIGNS.map((c, i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
                style={{ borderTop: "1px solid rgba(31,138,124,0.14)" }}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-gray-700"
                      >
                        {c.channel}
                      </span>
                      <h3 className="font-bold text-gray-800 mt-2 text-sm">{c.title}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{c.audience}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {[["Cost", c.cost], ["Effort", c.effort], ["Impact", c.impact]].map(([k, v]) => (
                      <div key={k} className="bg-gray-50 rounded-lg p-2">
                        <div className="text-xs text-gray-400">{k}</div>
                        <div className="text-sm font-bold text-gray-700">{v}</div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setActiveCampaign(activeCampaign === i ? null : i)}
                    data-testid={`campaign-expand-${i}`}
                    className="mt-3 flex items-center gap-1 text-xs font-semibold transition-colors"
                    style={{ color: "#1F8A7C" }}
                  >
                    {activeCampaign === i ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    {activeCampaign === i ? "Hide steps" : "See steps"}
                  </button>
                  {activeCampaign === i && (
                    <div className="mt-3 space-y-2">
                      {c.steps.map((step, j) => (
                        <div key={j} className="flex gap-2">
                          <span
                            className="text-xs font-bold shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white"
                            style={{ background: c.color, fontSize: 10 }}
                          >
                            {j + 1}
                          </span>
                          <p className="text-xs text-gray-600">{step}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* 90-day action plan */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h3 className="font-bold text-gray-800 mb-5">90-Day Action Sequence</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {ACTION_SEQUENCE.map((phase, i) => (
                <div
                  key={i}
                  className="border rounded-xl p-4"
                  style={{ borderColor: `${phase.color}40`, background: `${phase.color}08` }}
                >
                  <div
                    className="text-xs font-extrabold mb-1"
                    style={{ color: "#1F8A7C" }}
                  >
                    {phase.week}
                  </div>
                  <div className="text-sm font-bold text-gray-700 mb-3">{phase.title}</div>
                  <ul className="space-y-1.5">
                    {phase.steps.map((s, j) => (
                      <li key={j} className="text-xs text-gray-500 flex gap-1.5">
                        <span style={{ color: "#1F8A7C", fontWeight: 700 }}>›</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
